import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminBlogPost,
  deleteAdminBlogImage,
  deleteAdminBlogPost,
  getAdminBlogPosts,
  updateAdminBlogPost,
  uploadAdminBlogImage,
} from "../../../api/blogAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";
import AdminEditorModal from "./AdminEditorModal";
import AdminCollapsibleSection from "./AdminCollapsibleSection";
import AdminProductListCard from "./AdminProductListCard";
import {
  btnAccent,
  btnDanger,
  btnPrimary,
  btnSecondary,
  langTabActive,
  langTabIdle,
} from "./adminProductUi";
import { DashboardReloadButton } from "./dashboardUi";

const LANGS = ["en", "ar", "ku"];
const LANG_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };

const SECTION_TYPES = [
  { value: "paragraph", label: "Paragraph" },
  { value: "heading", label: "Heading" },
];

const emptyTranslation = () => ({ title: "", text: "", caption: "", sections: [] });

const emptyTranslations = () => ({
  en: emptyTranslation(),
  ar: emptyTranslation(),
  ku: emptyTranslation(),
});

const newSectionKey = () => `sec-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const normalizeSectionRow = (raw, idx) => {
  const bulletsRaw = raw?.bullets ?? raw?.Bullets;
  const bullets = Array.isArray(bulletsRaw)
    ? bulletsRaw
        .map((x) => (typeof x === "string" ? x : String(x?.text ?? x ?? "")).trim())
        .filter(Boolean)
    : [];
  const idRaw = raw?.id ?? raw?.Id;
  return {
    _key: idRaw != null ? `id-${idRaw}-${idx}` : newSectionKey(),
    id: idRaw != null && idRaw !== "" ? idRaw : null,
    displayOrder: Number(raw?.displayOrder ?? raw?.DisplayOrder ?? idx + 1) || idx + 1,
    sectionType:
      String(raw?.sectionType ?? raw?.SectionType ?? "paragraph")
        .toLowerCase()
        .trim() || "paragraph",
    title: String(raw?.title ?? raw?.Title ?? "").trim(),
    body: String(raw?.body ?? raw?.Body ?? "").trim(),
    bullets,
  };
};

const buildEmptyForm = () => ({
  publishedAtUtc: new Date().toISOString(),
  imageUrl: "",
  isActive: true,
  translations: emptyTranslations(),
});

const toInputDateTime = (isoValue) => {
  if (!isoValue) return "";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const hh = pad(date.getHours());
  const mm = pad(date.getMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
};

const toUtcIso = (inputValue) => {
  if (!inputValue) return new Date().toISOString();
  const date = new Date(inputValue);
  if (Number.isNaN(date.getTime())) return new Date().toISOString();
  return date.toISOString();
};

const normalizeTranslations = (translations) => {
  const byLang = emptyTranslations();
  const source = Array.isArray(translations)
    ? translations
    : Object.entries(translations || {}).map(([language, value]) => ({ language, ...(value || {}) }));
  source.forEach((tr) => {
    const lang = String(tr?.language || "")
      .toLowerCase()
      .split("-")[0];
    if (!LANGS.includes(lang)) return;
    const secSource = tr?.sections ?? tr?.Sections;
    const sections = Array.isArray(secSource)
      ? secSource.map((row, i) => normalizeSectionRow(row, i))
      : [];
    byLang[lang] = {
      title: tr?.title || tr?.Title || "",
      text: tr?.text || tr?.Text || "",
      caption: tr?.caption || tr?.Caption || "",
      sections,
    };
  });
  return byLang;
};

/** When API returns a flat post or root `sections` alongside `translations`. */
const mergeRootPostIntoTranslations = (post, byLang) => {
  const out = { ...byLang };
  const rootSections = Array.isArray(post?.sections ?? post?.Sections)
    ? post.sections ?? post.Sections
    : [];
  const hasTranslationsArray = Array.isArray(post?.translations) && post.translations.length > 0;

  if (!hasTranslationsArray) {
    out.en = {
      title: post?.title || post?.Title || "",
      text: post?.text || post?.Text || "",
      caption: post?.caption || post?.Caption || "",
      sections: rootSections.map((row, i) => normalizeSectionRow(row, i)),
    };
    return out;
  }

  if (rootSections.length && (!out.en.sections || out.en.sections.length === 0)) {
    out.en = {
      ...out.en,
      title: out.en.title || post?.title || "",
      text: out.en.text || post?.text || "",
      caption: out.en.caption || post?.caption || "",
      sections: rootSections.map((row, i) => normalizeSectionRow(row, i)),
    };
  }
  return out;
};

/** Only languages with a title are sent — title-only AR/KU is allowed. */
const toPayloadTranslations = (map) =>
  LANGS.filter((language) => String(map?.[language]?.title || "").trim()).map((language) => {
    const tr = map[language] || emptyTranslation();
    const sections = (tr.sections || []).map((s, i) => {
      const st = s.sectionType === "heading" ? "heading" : "paragraph";
      return {
        displayOrder: i + 1,
        sectionType: st,
        title: st === "heading" ? String(s.title || "").trim() || null : null,
        body: String(s.body || "").trim() || null,
        bullets: Array.isArray(s.bullets)
          ? s.bullets.map((b) => String(b || "").trim()).filter(Boolean)
          : [],
      };
    });
    return {
      language,
      title: String(tr.title || "").trim(),
      text: String(tr.text || "").trim() || null,
      caption: String(tr.caption || "").trim() || null,
      sections,
    };
  });

const apiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "";
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (data.title && !data.errors) return data.title;
  if (data.errors && typeof data.errors === "object") {
    return Object.entries(data.errors)
      .flatMap(([key, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]).map((m) => `${key}: ${m}`))
      .join(" · ");
  }
  return error?.message || "";
};

const postTitle = (post) => {
  const list = Array.isArray(post?.translations) ? post.translations : [];
  const en = list.find((tr) => String(tr.language || "").toLowerCase() === "en");
  return en?.title || list[0]?.title || post?.title || `Post #${post?.id}`;
};

const postLanguages = (post) => {
  const list = Array.isArray(post?.translations) ? post.translations : [];
  return LANGS.filter((lang) =>
    list.some(
      (tr) =>
        String(tr.language || "")
          .toLowerCase()
          .split("-")[0] === lang && String(tr.title || "").trim()
    )
  );
};

const BlogManager = () => {
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [posts, setPosts] = useState([]);
  const [createForm, setCreateForm] = useState(buildEmptyForm);
  const [createImageFile, setCreateImageFile] = useState(null);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [editingPostId, setEditingPostId] = useState(null);
  const [editForm, setEditForm] = useState(buildEmptyForm);
  const [editImageFile, setEditImageFile] = useState(null);
  const [activeEditLang, setActiveEditLang] = useState("en");

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await getAdminBlogPosts();
      setPosts(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load blog posts",
        message: apiErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const sortedPosts = useMemo(
    () =>
      [...posts].sort((a, b) => {
        const ad = new Date(a?.publishedAtUtc || 0).getTime();
        const bd = new Date(b?.publishedAtUtc || 0).getTime();
        return bd - ad;
      }),
    [posts]
  );

  const setLangField = (setForm, lang, partial) => {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...(prev.translations[lang] || emptyTranslation()), ...partial },
      },
    }));
  };

  const addSection = (setForm, lang, sectionType) => {
    setForm((prev) => {
      const tr = prev.translations[lang] || emptyTranslation();
      const sections = [...(tr.sections || [])];
      sections.push({
        _key: newSectionKey(),
        id: null,
        displayOrder: sections.length + 1,
        sectionType,
        title: "",
        body: "",
        bullets: [],
      });
      return {
        ...prev,
        translations: { ...prev.translations, [lang]: { ...tr, sections } },
      };
    });
  };

  const updateSection = (setForm, lang, index, patch) => {
    setForm((prev) => {
      const tr = prev.translations[lang] || emptyTranslation();
      const sections = (tr.sections || []).map((s, i) => (i === index ? { ...s, ...patch } : s));
      return {
        ...prev,
        translations: { ...prev.translations, [lang]: { ...tr, sections } },
      };
    });
  };

  const removeSection = (setForm, lang, index) => {
    setForm((prev) => {
      const tr = prev.translations[lang] || emptyTranslation();
      const sections = (tr.sections || []).filter((_, i) => i !== index);
      return {
        ...prev,
        translations: { ...prev.translations, [lang]: { ...tr, sections } },
      };
    });
  };

  const moveSection = (setForm, lang, index, delta) => {
    setForm((prev) => {
      const tr = prev.translations[lang] || emptyTranslation();
      const sections = [...(tr.sections || [])];
      const j = index + delta;
      if (j < 0 || j >= sections.length) return prev;
      const next = [...sections];
      [next[index], next[j]] = [next[j], next[index]];
      return {
        ...prev,
        translations: { ...prev.translations, [lang]: { ...tr, sections: next } },
      };
    });
  };

  const renderLangTabs = (activeLang, setActiveLang, form, keyPrefix) => (
    <div className="flex flex-wrap items-center gap-2">
      {LANGS.map((lang) => {
        const hasContent = Boolean((form.translations?.[lang]?.title || "").trim());
        return (
          <button
            key={`${keyPrefix}-${lang}`}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={activeLang === lang ? langTabActive : langTabIdle}
          >
            {LANG_LABELS[lang]}
            {hasContent ? " ●" : ""}
          </button>
        );
      })}
    </div>
  );

  const renderLangFields = (form, setForm, activeLang) => {
    const tr = form.translations[activeLang] || emptyTranslation();
    return (
      <div className="grid gap-3">
        <label className="text-sm text-slate-700">
          Title ({LANG_LABELS[activeLang]})
          <input
            type="text"
            value={tr.title}
            onChange={(e) => setLangField(setForm, activeLang, { title: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Legacy text (optional)
          <textarea
            value={tr.text}
            onChange={(e) => setLangField(setForm, activeLang, { text: e.target.value })}
            className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            placeholder="Optional if you use sections below"
          />
        </label>
        <label className="text-sm text-slate-700">
          Caption
          <input
            type="text"
            value={tr.caption}
            onChange={(e) => setLangField(setForm, activeLang, { caption: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">
            Sections ({LANG_LABELS[activeLang]})
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button
              type="button"
              onClick={() => addSection(setForm, activeLang, "paragraph")}
              className="text-xs px-2 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50"
            >
              + Paragraph
            </button>
            <button
              type="button"
              onClick={() => addSection(setForm, activeLang, "heading")}
              className="text-xs px-2 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50"
            >
              + Heading
            </button>
          </div>

          {(tr.sections || []).length === 0 ? (
            <p className="text-xs text-slate-400 italic mb-1">
              No sections yet — title alone is enough to save this language.
            </p>
          ) : null}

          {(tr.sections || []).map((sec, si) => (
            <div key={sec._key} className="rounded-lg border border-slate-200 bg-white p-2 mb-2 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-slate-600 flex items-center gap-1">
                  Type
                  <select
                    value={sec.sectionType === "heading" ? "heading" : "paragraph"}
                    onChange={(e) => {
                      const v = e.target.value;
                      updateSection(setForm, activeLang, si, {
                        sectionType: v,
                        ...(v === "heading" ? { bullets: [] } : {}),
                      });
                    }}
                    className="rounded border border-slate-300 px-1.5 py-1 text-xs bg-white"
                  >
                    {SECTION_TYPES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="text-[11px] text-slate-400">#{si + 1}</span>
                <div className="flex gap-1 ml-auto">
                  <button
                    type="button"
                    disabled={si === 0}
                    onClick={() => moveSection(setForm, activeLang, si, -1)}
                    className="text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    disabled={si >= (tr.sections || []).length - 1}
                    onClick={() => moveSection(setForm, activeLang, si, 1)}
                    className="text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSection(setForm, activeLang, si)}
                    className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    Remove
                  </button>
                </div>
              </div>

              {sec.sectionType === "heading" ? (
                <input
                  type="text"
                  value={sec.title}
                  onChange={(e) => updateSection(setForm, activeLang, si, { title: e.target.value })}
                  placeholder="Heading title"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              ) : null}

              <textarea
                value={sec.body}
                onChange={(e) => updateSection(setForm, activeLang, si, { body: e.target.value })}
                placeholder={
                  sec.sectionType === "heading"
                    ? "Optional subtitle"
                    : "Paragraph body"
                }
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[72px]"
              />

              {sec.sectionType === "paragraph" ? (
                <textarea
                  value={(sec.bullets || []).join("\n")}
                  onChange={(e) => {
                    const bullets = e.target.value
                      .split(/\r?\n/)
                      .map((line) => line.trim())
                      .filter(Boolean);
                    updateSection(setForm, activeLang, si, { bullets });
                  }}
                  placeholder="Bullets (optional) — one per line"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[80px] font-mono text-xs"
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleCreate = async () => {
    const anyTitle = LANGS.some((lang) => (createForm.translations[lang]?.title || "").trim());
    if (!anyTitle) {
      return notify({ type: "error", title: "At least one language title is required" });
    }

    const payload = {
      publishedAtUtc: toUtcIso(createForm.publishedAtUtc),
      imageUrl: createForm.imageUrl || null,
      isActive: Boolean(createForm.isActive),
      translations: toPayloadTranslations(createForm.translations),
    };

    setCreating(true);
    try {
      const created = await createAdminBlogPost(payload);
      if (created?.id && createImageFile) {
        await uploadAdminBlogImage({ id: created.id, file: createImageFile });
      }
      notify({ type: "success", title: "Blog post saved for all languages" });
      setCreateForm(buildEmptyForm());
      setCreateImageFile(null);
      setActiveCreateLang("en");
      await loadPosts();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: apiErrorMessage(error),
      });
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (post) => {
    setEditingPostId(post.id);
    setActiveEditLang("en");
    setEditImageFile(null);
    setEditForm({
      publishedAtUtc: post?.publishedAtUtc || new Date().toISOString(),
      imageUrl: post?.imageUrl || "",
      isActive: Boolean(post?.isActive),
      translations: mergeRootPostIntoTranslations(post, normalizeTranslations(post?.translations)),
    });
  };

  const closeEdit = () => {
    setEditingPostId(null);
    setEditForm(buildEmptyForm());
    setEditImageFile(null);
    setActiveEditLang("en");
  };

  const saveEdit = async () => {
    if (!editingPostId) return;
    const anyTitle = LANGS.some((lang) => (editForm.translations[lang]?.title || "").trim());
    if (!anyTitle) {
      return notify({ type: "error", title: "At least one language title is required" });
    }

    setSaving(true);
    try {
      let nextImageUrl = editForm.imageUrl || null;
      if (editImageFile) {
        const uploaded = await uploadAdminBlogImage({
          id: editingPostId,
          file: editImageFile,
        });
        nextImageUrl = uploaded?.imageUrl || nextImageUrl;
      }

      await updateAdminBlogPost({
        id: editingPostId,
        payload: {
          publishedAtUtc: toUtcIso(editForm.publishedAtUtc),
          imageUrl: nextImageUrl,
          isActive: Boolean(editForm.isActive),
          translations: toPayloadTranslations(editForm.translations),
        },
      });
      notify({ type: "success", title: "Blog post updated for all languages" });
      closeEdit();
      await loadPosts();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: apiErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const removePost = async (post) => {
    if (!window.confirm(`Delete post "${postTitle(post)}"?`)) return;
    try {
      await deleteAdminBlogPost({ id: post.id });
      notify({ type: "success", title: "Blog post deleted" });
      if (editingPostId === post.id) closeEdit();
      await loadPosts();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: apiErrorMessage(error),
      });
    }
  };

  const editPreviewUrl = useMemo(() => resolveMediaUrl(editForm.imageUrl), [editForm.imageUrl]);

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadPosts} loading={loading} label="Reload list" />
      </div>

      <AdminCollapsibleSection
        title="Add new blog post"
        description="Fill English / Arabic / Kurdish tabs. Title alone is enough per language — sections are optional."
        badge="Create"
        footer={
          <button type="button" disabled={creating} onClick={handleCreate} className={btnAccent}>
            {creating ? "Saving..." : "Save post (all languages)"}
          </button>
        }
      >
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Published at
              <input
                type="datetime-local"
                value={toInputDateTime(createForm.publishedAtUtc)}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, publishedAtUtc: e.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700 sm:mt-6">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(createForm.isActive)}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, isActive: e.target.checked }))
                }
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this post from the public site.
                </span>
              </span>
            </label>
            <label className="text-sm text-slate-700 sm:col-span-2">
              Post image (shared across languages)
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCreateImageFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm"
              />
              <span className="mt-1 block text-xs text-slate-500">
                {createImageFile
                  ? `Selected: ${createImageFile.name} — uploads after save.`
                  : "Optional. Uploads after the post is saved."}
              </span>
            </label>
          </div>

          {renderLangTabs(activeCreateLang, setActiveCreateLang, createForm, "create")}
          {renderLangFields(createForm, setCreateForm, activeCreateLang)}
        </div>
      </AdminCollapsibleSection>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-[#011337]">Your blog posts</h3>
          {!loading ? (
            <span className="text-sm text-slate-500">
              {sortedPosts.length} item{sortedPosts.length !== 1 ? "s" : ""}
            </span>
          ) : null}
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-slate-200 bg-white">
            Loading posts...
          </p>
        ) : sortedPosts.length ? (
          <div className="grid gap-3">
            {sortedPosts.map((post) => {
              const langs = postLanguages(post);
              const missing = LANGS.filter((lang) => !langs.includes(lang));
              return (
                <AdminProductListCard
                  key={post.id}
                  id={post.id}
                  title={postTitle(post)}
                  meta={[
                    post.publishedAtUtc
                      ? `Published: ${new Date(post.publishedAtUtc).toLocaleString()}`
                      : null,
                    `Languages: ${langs.map((l) => l.toUpperCase()).join(", ") || "—"}`,
                    missing.length
                      ? `Missing: ${missing.map((l) => l.toUpperCase()).join(", ")}`
                      : null,
                  ].filter(Boolean)}
                  status={post.isActive !== false}
                  imageUrl={post.imageUrl}
                  isEditing={editingPostId === post.id}
                  onEdit={() => openEdit(post)}
                  onDelete={() => removePost(post)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
            No blog posts yet. Use &quot;Add new blog post&quot; above.
          </p>
        )}
      </div>

      <AdminEditorModal
        open={Boolean(editingPostId)}
        title={`Edit post: ${postTitle(posts.find((p) => p.id === editingPostId) || {})}`}
        subtitle="Edit every language, then save. Title alone is enough per language."
        onClose={closeEdit}
        footer={
          <>
            <button type="button" onClick={closeEdit} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-3">
              Post image (shared across languages)
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              {editPreviewUrl ? (
                <img
                  src={editPreviewUrl}
                  alt=""
                  className="w-full sm:w-40 h-28 object-cover rounded-lg border border-slate-200 bg-white"
                />
              ) : (
                <div className="w-full sm:w-40 h-28 rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center text-xs text-slate-400">
                  No image
                </div>
              )}
              <div className="flex-1 min-w-0 space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditImageFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm"
                />
                {editImageFile?.name ? (
                  <p className="text-xs text-slate-500">
                    Selected: {editImageFile.name} — uploads when you save.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">Choose a file to replace the current image.</p>
                )}
                {editForm.imageUrl ? (
                  <button
                    type="button"
                    className={btnDanger}
                    onClick={async () => {
                      try {
                        await deleteAdminBlogImage({ id: editingPostId });
                        await updateAdminBlogPost({
                          id: editingPostId,
                          payload: { imageUrl: null },
                        });
                        setEditForm((prev) => ({ ...prev, imageUrl: "" }));
                        setEditImageFile(null);
                        notify({ type: "success", title: "Image removed" });
                        await loadPosts();
                      } catch (error) {
                        notify({
                          type: "error",
                          title: "Remove image failed",
                          message: apiErrorMessage(error),
                        });
                      }
                    }}
                  >
                    Remove image
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Published at
              <input
                type="datetime-local"
                value={toInputDateTime(editForm.publishedAtUtc)}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, publishedAtUtc: e.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700 sm:mt-6">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(editForm.isActive)}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, isActive: e.target.checked }))
                }
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this post from the public site.
                </span>
              </span>
            </label>
          </div>

          {renderLangTabs(activeEditLang, setActiveEditLang, editForm, "edit")}
          {renderLangFields(editForm, setEditForm, activeEditLang)}
        </div>
      </AdminEditorModal>
    </section>
  );
};

export default BlogManager;
